import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { presenceService } from '@/services/core/presence.service'

const PING_MS = 30_000
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart', 'focus'] as const

/** One id per loaded page: a reload or a duplicated tab is a new tab to the server. */
const TAB_ID = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 32)

/**
 * Tells the server this tab is open (POST /presence/heartbeat): every 30 s,
 * right away on a page change, with the seconds since the last keyboard,
 * mouse, touch or scroll input. The server shows the person idle after 5 min
 * without input. Closing the tab sends a keepalive «leave». A failed ping is
 * dropped: the next one catches up, and a tab silent for 150 s just goes
 * offline.
 */
export function usePresenceHeartbeat() {
  const { pathname } = useLocation()
  // Set on mount by the page effect below.
  const lastInput = useRef(0)
  const path = useRef(pathname)

  useEffect(() => {
    const idleSeconds = () => Math.floor((Date.now() - lastInput.current) / 1000)
    const ping = () => {
      presenceService.heartbeat({ tab_id: TAB_ID, path: path.current, idle_seconds: idleSeconds() }).catch(() => {})
    }
    let lastMove = 0
    const onInput = (event: Event) => {
      // pointermove fires per pixel: one timestamp a second is enough.
      if (event.type === 'pointermove' && event.timeStamp - lastMove < 1000) return
      lastMove = event.timeStamp
      lastInput.current = Date.now()
    }
    const onHide = () => {
      presenceService.leave({ tab_id: TAB_ID, idle_seconds: idleSeconds() }).catch(() => {})
    }

    INPUT_EVENTS.forEach((name) => window.addEventListener(name, onInput, { passive: true, capture: true }))
    window.addEventListener('pagehide', onHide)
    const timer = window.setInterval(ping, PING_MS)
    return () => {
      window.clearInterval(timer)
      INPUT_EVENTS.forEach((name) => window.removeEventListener(name, onInput, { capture: true }))
      window.removeEventListener('pagehide', onHide)
    }
  }, [])

  useEffect(() => {
    path.current = pathname
    lastInput.current = Date.now()
    presenceService
      .heartbeat({ tab_id: TAB_ID, path: pathname, idle_seconds: 0 })
      .catch(() => {})
  }, [pathname])
}
